import GraphQLCRUD from '@/se/components/GraphQLCRUD';
import scheme from '../../../graph/config';
import { compose, withProps } from 'recompose';
import { withLabel } from '@/se/components/Label';
import TextInput from '@/se/components/inputs/TextInput';
import SelectInput from '@/se/components/inputs/SelectInput';
import ObjectInput from '@/se/components/inputs/ObjectInput';
import { H2 } from '@/se/components/typography';
import React from 'react';
import { graphql } from '@apollo/client/react/hoc';
import { ConfigType } from '../../../types/HospitalConfig';
import ExpandableText from '../../core/ExpandableText';
import { RoleClassificationLabel } from '@/types/StaffMember';

const Input = withProps({
  schema: {
    title: compose(
      withLabel('Staff Title'),
      withProps({
        placeholder: 'e.g., Nurse Practitioner',
        autoComplete: 'off',
        required: true,
      })
    )(TextInput),
    abbr: compose(
      withLabel('Abbreviation'),
      withProps({
        placeholder: 'e.g., NP',
        autoComplete: 'off',
        required: true,
      })
    )(TextInput),
    roleClassification: compose(
      withLabel('Role Classification'),
      withProps({
        options: RoleClassificationLabel,
        placeholder: 'Nurse / Technician',
      })
    )(SelectInput),
    department: compose(
      withLabel('Department'),
      withProps({
        placeholder: 'e.g., Surgery',
        autoComplete: 'off',
      })
    )(TextInput),
  },
})(ObjectInput);

export const titleColumn = {
  title: 'Staff Title',
  key: 'title',
  lens: item => JSON.parse(item.values || '{}')?.title,
  Component: props => <ExpandableText text={props.data} />,
};

export const abbrColumn = {
  title: 'Abbreviation',
  key: 'abbr',
  lens: item => JSON.parse(item.values || '{}')?.abbr,
  Component: props => <ExpandableText text={props.data} />,
};

export const roleClassificationColumn = {
  title: 'Role Classification',
  key: 'roleClassification',
  lens: item => JSON.parse(item.values || '{}')?.roleClassification,
  Component: props => <ExpandableText text={(props.data && RoleClassificationLabel[props.data]) || '-'} />,
};

export const departmentColumn = {
  title: 'Department',
  key: 'department',
  lens: item => JSON.parse(item.values || '{}')?.department,
  Component: props => <ExpandableText text={props.data || '-'} />,
};

const columns = [titleColumn, abbrColumn, roleClassificationColumn, departmentColumn];

const prepareCreateData = formData => ({
  type: ConfigType.StaffTitle,
  values: JSON.stringify(formData),
});

const prepareUpdateData = formData => ({
  id: formData?.id,
  values: JSON.stringify(formData),
});

export const mapEditItemProps = ({ data }) => {
  if (data) {
    const obj = {
      ...data,
      config: {
        ...data.config,
        ...JSON.parse(data.config.values || '{}'),
      },
    };
    return { data: obj };
  }
};

const Title = () => <H2>{'Staff Titles Config'}</H2>;

const StaffTitlesConfig = GraphQLCRUD({
  entityName: 'Config',
  scheme,
  List: {
    tableKey: 'Config',
    columns,
    Title,
    itemsProvider: graphql(scheme.list, {
      options: ({ idProvider, ...rest }) => ({
        variables: {
          type: ConfigType.StaffTitle,
          fetchPolicy: 'cache-and-network',
        },
      }),
    }),
  },
  Create: {
    Input,
    prepareCreateData,
    refetchQueries: [{ query: scheme.list, variables: { type: ConfigType.StaffTitle } }],
  },
  Show: {
    columns,
  },
  Edit: {
    Input,
    mapEditItemProps,
    prepareUpdateData,
    refetchQueries: [{ query: scheme.list, variables: { type: ConfigType.StaffTitle } }],
    removeRefetchQueries: [{ query: scheme.list, variables: { type: ConfigType.StaffTitle } }],
  },
});

export default StaffTitlesConfig;
